import { useMemo, useState } from "react";
import { Sparkles, Check, Plus, Loader2, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { useRecommendations, type EvaluatedRule } from "@/hooks/use-recommendations";
import { Link } from "react-router-dom";
import { productPath } from "@/lib/url-slugs";

interface Props {
  placement: "product_page" | "cart_drawer" | "cart_page";
  triggerProduct?: { id: string; brand: string; category_id?: string; product_type?: string };
  compact?: boolean;
}

const RecommendationSection = ({ placement, triggerProduct, compact }: Props) => {
  const { items, addToCart } = useCart();
  const { lang } = useLanguage();
  const [addingId, setAddingId] = useState<string | null>(null);

  const effectiveItems = useMemo(() => {
    if (placement !== "product_page" || !triggerProduct) return items;
    if (items.some((i) => i.product.id === triggerProduct.id)) return items;
    return [
      ...items,
      {
        product: {
          id: triggerProduct.id,
          brand: triggerProduct.brand,
          category_id: triggerProduct.category_id,
          product_type: triggerProduct.product_type,
          name: "", slug: "", description: "", category: "", categorySlug: "",
          price: 0, currency: "EUR", stock: 0, sku: "", specs: {}, images: [], status: "active" as const,
        } as any,
        quantity: 1,
      },
    ];
  }, [items, triggerProduct, placement]);

  const { evaluated } = useRecommendations(effectiveItems);

  const visible = evaluated.filter((e) => {
    if (!e.triggerMatched) return false;
    if (placement === "product_page" && !e.rule.display_product_page) return false;
    if (placement === "cart_drawer" && !e.rule.display_cart_drawer) return false;
    if (placement === "cart_page" && !e.rule.display_cart_page) return false;
    return e.recommendedProducts.length > 0;
  });

  if (visible.length === 0) return null;

  const title = (e: EvaluatedRule) => (lang === "lv" ? e.rule.title_lv : e.rule.title_en) || e.rule.name;
  const desc = (e: EvaluatedRule) => (lang === "lv" ? e.rule.description_lv : e.rule.description_en);
  const addLabel = (e: EvaluatedRule) =>
    (lang === "lv" ? e.rule.add_button_text_lv : e.rule.add_button_text_en) || (lang === "lv" ? "Pievienot" : "Add");
  const completedMsg = (e: EvaluatedRule) =>
    (lang === "lv" ? e.rule.completed_message_lv : e.rule.completed_message_en) || (lang === "lv" ? "Komplekts pievienots" : "Bundle added");

  const handleAdd = async (product: any, qty: number) => {
    setAddingId(product.id);
    try {
      await Promise.resolve(addToCart(product, qty));
    } finally {
      setTimeout(() => setAddingId(null), 250);
    }
  };

  // Product page uses the compact stacked-row layout
  if (placement === "product_page") {
    return (
      <div className="space-y-3">
        {visible.map((e) => (
          <div
            key={e.rule.id}
            className="rounded-xl border bg-muted/30 p-3"
          >
            <div className="flex items-start gap-2 mb-2.5">
              <Sparkles size={14} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-sm leading-tight">{title(e)}</p>
                {desc(e) && <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{desc(e)}</p>}
              </div>
            </div>

            <div className="space-y-1.5">
              {e.recommendedProducts.map((rp) => {
                const p = rp.product!;
                const inCart = items.some((i) => i.product.id === p.id);
                const oos = p.stock <= 0;
                const disc =
                  (p as any).compareAtPrice && (p as any).compareAtPrice > p.price
                    ? Math.round((((p as any).compareAtPrice - p.price) / (p as any).compareAtPrice) * 100)
                    : 0;
                const busy = addingId === p.id;
                return (
                  <div
                    key={rp.id}
                    className="flex items-center gap-2.5 rounded-lg bg-background border p-1.5 sm:p-2"
                  >
                    <Link
                      to={productPath(lang, p.slug)}
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-md bg-white border shrink-0 overflow-hidden flex items-center justify-center"
                    >
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-contain" loading="lazy" />
                      ) : (
                        <span className="text-[10px] text-muted-foreground">{p.brand}</span>
                      )}
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={productPath(lang, p.slug)}
                        className="block text-xs sm:text-sm font-medium leading-tight line-clamp-2 hover:text-primary transition-colors"
                      >
                        {p.name}
                      </Link>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span className="text-sm font-bold text-primary">€{p.price.toFixed(2)}</span>
                        {(p as any).compareAtPrice && (
                          <span className="text-[11px] text-muted-foreground line-through">€{(p as any).compareAtPrice.toFixed(2)}</span>
                        )}
                        {disc > 0 && (
                          <span className="bg-destructive text-destructive-foreground text-[10px] font-bold px-1 py-px rounded leading-none">
                            −{disc}%
                          </span>
                        )}
                        {!oos && (
                          <span className="hidden sm:inline-flex text-[10px] items-center gap-1 text-emerald-600 ml-1">
                            <span className="w-1 h-1 rounded-full bg-emerald-500" />
                            {lang === "lv" ? "Noliktavā" : "In stock"}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={inCart ? "secondary" : "default"}
                      disabled={inCart || oos || busy}
                      onClick={() => handleAdd(p, rp.default_qty || 1)}
                      className="h-8 px-2.5 text-xs shrink-0"
                    >
                      {oos ? (
                        lang === "lv" ? "Nav" : "Out"
                      ) : busy ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : inCart ? (
                        <><Check size={12} className="mr-1" />{lang === "lv" ? "Grozā" : "Added"}</>
                      ) : (
                        <><Plus size={12} className="mr-0.5" />{addLabel(e)}</>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>

            {e.bundleComplete && e.rule.discount_type !== "none" && (
              <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-primary">
                <Check size={12} />
                <span>{completedMsg(e)}</span>
                {e.discountAmount > 0 && (
                  <span className="text-muted-foreground">
                    · −€{e.discountAmount.toFixed(2)}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Cart drawer / cart page: keep prior grid layout (unchanged behaviour)
  return (
    <div className={compact ? "space-y-4" : "space-y-6"}>
      {visible.map((e) => {
        const alreadyIn = (pid: string) => items.some((i) => i.product.id === pid);
        return (
          <div
            key={e.rule.id}
            className="rounded-2xl border border-primary/20 bg-primary/5 p-3 sm:p-4"
          >
            <div className="flex items-start gap-2 mb-3">
              <Sparkles size={16} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-sm sm:text-base leading-tight">{title(e)}</p>
                {desc(e) && <p className="text-xs text-muted-foreground mt-0.5">{desc(e)}</p>}
                {e.bundleComplete && e.rule.discount_type !== "none" && (
                  <p className="text-xs font-medium text-primary mt-1 flex items-center gap-1">
                    <Check size={12} /> {completedMsg(e)}
                    {e.discountAmount > 0 && ` (−€${e.discountAmount.toFixed(2)})`}
                  </p>
                )}
              </div>
            </div>

            <div className={compact ? "grid grid-cols-2 gap-2" : "grid grid-cols-2 sm:grid-cols-3 gap-3"}>
              {e.recommendedProducts.map((rp) => {
                const p = rp.product!;
                const inCart = alreadyIn(p.id);
                return (
                  <div key={rp.id} className="rounded-xl border bg-background p-2 flex flex-col">
                    <div className="aspect-square w-full rounded-lg bg-muted overflow-hidden mb-2">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-contain" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">{p.brand}</div>
                      )}
                    </div>
                    <p className="text-xs font-medium leading-tight line-clamp-2 mb-1">{p.name}</p>
                    <p className="text-sm font-bold text-primary mb-2">€{p.price.toFixed(2)}</p>
                    <Button
                      size="sm"
                      variant={inCart ? "secondary" : "default"}
                      disabled={inCart || p.stock <= 0}
                      onClick={() => addToCart(p, rp.default_qty || 1)}
                      className="w-full text-xs mt-auto"
                    >
                      {inCart ? <><Check size={12} className="mr-1" /> {lang === "lv" ? "Grozā" : "Added"}</> : <><Plus size={12} className="mr-1" /> {addLabel(e)}</>}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RecommendationSection;
